import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";

const withEstablishmentPrefix = (
  path: string,
  establishmentSlug?: string | number | null,
) => {
  if (!establishmentSlug) return path;

  const baseURL = axiosInstance.defaults.baseURL || "";
  const alreadyScoped =
    path.startsWith("/establishment/") || baseURL.includes("/establishment/");

  return alreadyScoped ? path : `/establishment/${establishmentSlug}${path}`;
};

// Buscar token do cartão
export const fetchClientCardToken = createAsyncThunk(
  "clients/fetchClientCardToken",
  async (
    {
      clientId,
      establishmentSlug,
    }: { clientId: string | number; establishmentSlug?: string | number },
    thunkAPI,
  ) => {
    try {
      const path = withEstablishmentPrefix(
        `/clients/${clientId}/cards`,
        establishmentSlug,
      );
      const response = await axiosInstance.get(path);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
      return thunkAPI.rejectWithValue({
        error: "Erro desconhecido ao buscar o token do cartão do cliente",
      });
    }
  },
);

// Criar token do cartão
export const createClientCardToken = createAsyncThunk(
  "clients/createClientCardToken",
  async (
    {
      clientId,
      data,
      establishmentSlug,
    }: {
      clientId: string | number;
      data: any;
      establishmentSlug?: string | number;
    },
    thunkAPI,
  ) => {
    try {
      const path = withEstablishmentPrefix(
        `/clients/${clientId}/card-token`,
        establishmentSlug,
      );
      const response = await axiosInstance.post(path, data);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
      return thunkAPI.rejectWithValue({
        error: "Erro desconhecido ao criar token do cartão do cliente",
      });
    }
  },
);

// Atualizar token do cartão
export const updateClientCardToken = createAsyncThunk(
  "clients/updateClientCardToken",
  async (
    {
      clientId,
      data,
      establishmentSlug,
    }: {
      clientId: string | number;
      data: any;
      establishmentSlug?: string | number;
    },
    thunkAPI,
  ) => {
    try {
      const path = withEstablishmentPrefix(
        `/clients/${clientId}/card-token`,
        establishmentSlug,
      );
      const response = await axiosInstance.put(path, data);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
      return thunkAPI.rejectWithValue({
        error: "Erro desconhecido ao atualizar token do cartão do cliente",
      });
    }
  },
);

// Excluir token do cartão
export const deleteClientCardToken = createAsyncThunk(
  "clients/deleteClientCardToken",
  async (
    {
      clientId,
      clientCardToken,
      establishment,
      establishmentSlug,
    }: {
      clientId: string | number;
      clientCardToken?: string | number | null;
      establishment?: string | number | null;
      establishmentSlug?: string | number | null;
    },
    thunkAPI,
  ) => {
    try {
      const slug = establishmentSlug || establishment;

      // If a specific card token id is provided and establishment is present,
      // call the admin route that includes the card-token id
      if (clientCardToken) {
        // axiosInstance.baseURL is typically set to include the establishment
        // (see AuthContext -> baseURL = /establishment/{slug}), so call the
        // client-scoped path relative to baseURL to avoid duplicating
        // the `establishment` segment.
        const path = withEstablishmentPrefix(
          `/clients/${clientId}/card-token/${clientCardToken}`,
          slug,
        );
        const response = await axiosInstance.delete(path);
        return response.data;
      }

      // Fallback to legacy route (client-portal style) which deletes token by client
      const path = withEstablishmentPrefix(
        `/clients/${clientId}/card-token`,
        slug,
      );
      const response = await axiosInstance.delete(path);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
      return thunkAPI.rejectWithValue({
        error: "Erro desconhecido ao excluir token do cartão do cliente",
      });
    }
  },
);
